(() => {
  'use strict';

  const configuration = document.querySelector('script[data-yesgk-filters]');
  if (!configuration) return;
  const labels = configuration.dataset;
  const states = new Map();
  const observers = new WeakMap();
  const sidebarScroll = new WeakMap();
  let pendingFocus = null;
  let stickyHeader = null;
  let headerSizeObserver = null;
  let headerClassObserver = null;
  let viewportConnected = false;
  let viewportFrame = 0;

  function updateStickyOffset() {
    if (!window.matchMedia('(min-width: 1025px)').matches) return;
    const header = document.getElementById('header');
    const height = header && header.classList.contains('stickyHeader') ? header.getBoundingClientRect().height : 0;
    const stickyTop = Math.ceil(height + 16);
    const previewBar = document.getElementById('PBarNextFrameWrapper');
    const previewBounds = previewBar ? previewBar.getBoundingClientRect() : null;
    for (const sidebar of document.querySelectorAll('.sidebar.filterbar.left:has(.yg-filters)')) {
      sidebar.style.setProperty('--yg-sticky-top', stickyTop + 'px');
      const bounds = sidebar.getBoundingClientRect();
      let bottom = window.innerHeight;
      if (previewBounds && previewBounds.height > 0 && previewBounds.bottom > 0 &&
          previewBounds.left < bounds.right && previewBounds.right > bounds.left) {
        bottom = Math.min(bottom, previewBounds.top);
      }
      const top = Math.max(bounds.top, stickyTop, 0);
      sidebar.style.setProperty('--yg-available-height', Math.max(0, Math.floor(bottom - top - 16)) + 'px');
    }
  }

  function queueViewportUpdate() {
    if (viewportFrame) return;
    viewportFrame = requestAnimationFrame(() => {
      viewportFrame = 0;
      updateStickyOffset();
    });
  }

  function connectViewport() {
    if (viewportConnected) return;
    viewportConnected = true;
    window.addEventListener('scroll', queueViewportUpdate, { passive: true });
    window.addEventListener('resize', queueViewportUpdate, { passive: true });
    window.addEventListener('load', queueViewportUpdate, { once: true });
    const previewWatcher = new MutationObserver(watchPreviewBar);
    function watchPreviewBar() {
      const previewBar = document.getElementById('PBarNextFrameWrapper');
      if (!previewBar) return;
      new ResizeObserver(queueViewportUpdate).observe(previewBar);
      previewWatcher.disconnect();
      queueViewportUpdate();
    }
    previewWatcher.observe(document.body, { childList: true });
    watchPreviewBar();
  }

  function connectStickyHeader() {
    const header = document.getElementById('header');
    if (header !== stickyHeader) {
      if (headerSizeObserver) headerSizeObserver.disconnect();
      if (headerClassObserver) headerClassObserver.disconnect();
      stickyHeader = header;
      if (header) {
        headerSizeObserver = new ResizeObserver(updateStickyOffset);
        headerSizeObserver.observe(header);
        headerClassObserver = new MutationObserver(updateStickyOffset);
        headerClassObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
      }
    }
    updateStickyOffset();
  }

  function remember(group) {
    if (!group.isConnected) return;
    const previous = states.get(group.dataset.ygKey) || {};
    const search = group.querySelector('.yg-filter-search');
    const options = group.querySelector('.yg-filter-options');
    states.set(group.dataset.ygKey, {
      open: group.open,
      query: search ? search.value : '',
      scroll: group.open && options ? options.scrollTop : (previous.scroll || 0)
    });
  }

  function filterOptions(group) {
    const search = group.querySelector('.yg-filter-search');
    if (!search) return;
    group.querySelector('.yg-filter-search-clear').hidden = search.value.length === 0;
    const query = search.value.trim().normalize('NFKC').toLocaleLowerCase();
    let matches = 0;
    for (const row of group.querySelector('.yg-filter-options').children) {
      const optionLabel = row.querySelector('.yg-filter-option-label') || row;
      const match = optionLabel.textContent.normalize('NFKC').toLocaleLowerCase().includes(query);
      if (match) matches += 1;
      row.hidden = !match && !row.querySelector('input:checked');
    }
    group.querySelector('.yg-filter-empty').hidden = !query || matches > 0;
  }

  function enhance(form) {
    if (form.classList.contains('yg-filters')) return;
    const originalGroups = Array.from(form.querySelectorAll(':scope > .filterBox'));
    if (!originalGroups.length) return;
    const sidebar = form.closest('.sidebar.filterbar');
    const scroll = sidebar ? sidebarScroll.get(sidebar) : undefined;
    form.classList.add('yg-filters');

    const header = document.createElement('div');
    header.className = 'yg-filter-header';
    const heading = document.createElement('h2');
    heading.textContent = labels.title;
    header.append(heading);
    const nativeReset = document.querySelector('.active-facets > a.actFilter');
    if (nativeReset) {
      const reset = document.createElement('a');
      reset.className = 'actFilter yg-filter-reset';
      reset.href = nativeReset.getAttribute('href');
      reset.textContent = labels.reset;
      header.append(reset);
    }
    form.prepend(header);

    for (const original of originalGroups) {
      const title = original.querySelector(':scope > .widget-title');
      const input = original.querySelector('input[name]');
      if (!title || !input) continue;
      const parameter = input.name;
      const key = window.location.pathname + ':' + parameter;
      const isPrice = original.classList.contains('flPrice');
      const isStock = parameter === 'filter.v.availability';
      const isBrand = parameter === 'filter.p.vendor';
      const isType = parameter === 'filter.p.product_type';
      const titleCopy = title.cloneNode(true);
      titleCopy.querySelectorAll('.count-bubble').forEach(node => node.remove());
      const name = titleCopy.textContent.trim();
      const previous = states.get(key);
      const group = document.createElement('details');
      for (const attribute of original.attributes) {
        if (attribute.name !== 'open') group.setAttribute(attribute.name, attribute.value);
      }
      group.classList.add('yg-filter-group');
      group.dataset.ygKey = key;
      group.style.order = isStock ? '1' : isPrice ? '2' : isType ? '3' : isBrand ? '4' : '5';
      group.open = previous ? previous.open : true;

      const summary = document.createElement('summary');
      summary.className = title.className + ' yg-filter-title';
      while (title.firstChild) summary.append(title.firstChild);
      for (const state of ['plus', 'minus']) {
        const icon = document.createElement('i');
        icon.className = 'at at-' + state + '-l yg-filter-' + state;
        icon.setAttribute('aria-hidden', 'true');
        summary.append(icon);
      }
      title.remove();
      const body = document.createElement('div');
      body.className = 'yg-filter-body';
      while (original.firstChild) body.append(original.firstChild);
      group.append(summary, body);
      original.replaceWith(group);

      const options = body.querySelector('ul.flOpt');
      if (options) {
        options.classList.add('yg-filter-options');
        if (isBrand || isType) {
          const search = document.createElement('input');
          search.type = 'search';
          search.className = 'yg-filter-search';
          search.placeholder = labels.search.replace('__FILTER__', isType ? 'franchises' : name);
          search.setAttribute('aria-label', search.placeholder);
          search.autocomplete = 'off';
          search.value = previous ? previous.query : '';
          const searchField = document.createElement('div');
          searchField.className = 'yg-filter-search-field';
          const clear = document.createElement('button');
          clear.type = 'button';
          clear.className = 'yg-filter-search-clear';
          clear.textContent = '×';
          clear.setAttribute('aria-label', labels.clear + ': ' + search.placeholder);
          clear.title = labels.clear;
          searchField.append(search, clear);
          const empty = document.createElement('p');
          empty.className = 'yg-filter-empty';
          empty.setAttribute('role', 'status');
          empty.textContent = labels.empty;
          empty.hidden = true;
          options.before(searchField);
          options.after(empty);
          search.addEventListener('input', () => {
            filterOptions(group);
            options.scrollTop = 0;
            remember(group);
          });
          search.addEventListener('keydown', event => {
            if (event.key === 'Enter') event.preventDefault();
          });
          clear.addEventListener('click', () => {
            search.value = '';
            search.dispatchEvent(new Event('input', { bubbles: true }));
            search.focus({ preventScroll: true });
          });
          filterOptions(group);
        }
        options.scrollTop = previous ? previous.scroll : 0;
        options.addEventListener('scroll', () => remember(group), { passive: true });
      }
      summary.addEventListener('click', () => {
        remember(group);
        states.get(key).open = !group.open;
      });
      group.addEventListener('toggle', () => {
        if (!group.isConnected) return;
        if (group.open && options) options.scrollTop = (states.get(key) || {}).scroll || 0;
        remember(group);
      });
      remember(group);
    }

    if (sidebar && scroll !== undefined && window.matchMedia('(min-width: 1025px)').matches) {
      sidebar.scrollTop = scroll;
    }
    if (pendingFocus && document.activeElement === document.body) {
      const target = Array.from(form.elements).find(element =>
        element.name === pendingFocus.name &&
        (pendingFocus.type === 'number' || element.value === pendingFocus.value)
      );
      if (target && !target.disabled) target.focus({ preventScroll: true });
    }
    pendingFocus = null;
    queueViewportUpdate();
  }

  function connect() {
    for (const container of document.querySelectorAll('.sidebar_tags')) {
      const sidebar = container.closest('.sidebar.filterbar');
      if (sidebar && !sidebarScroll.has(sidebar)) {
        sidebarScroll.set(sidebar, sidebar.scrollTop);
        sidebar.addEventListener('scroll', () => sidebarScroll.set(sidebar, sidebar.scrollTop), { passive: true });
      }
      const form = container.querySelector('#CollectionFiltersForm');
      if (form) enhance(form);
      if (observers.has(container)) continue;
      const observer = new MutationObserver(() => {
        const replacement = container.querySelector('#CollectionFiltersForm');
        if (replacement) enhance(replacement);
      });
      observer.observe(container, { childList: true });
      observers.set(container, observer);
    }
    connectStickyHeader();
    connectViewport();
  }

  document.addEventListener('change', event => {
    const input = event.target;
    const form = input.closest('#CollectionFiltersForm.yg-filters');
    if (!form || !input.matches('.custCheck, .prRange, #SortBy')) return;
    const sidebar = form.closest('.sidebar.filterbar');
    if (sidebar) sidebarScroll.set(sidebar, sidebar.scrollTop);
    form.querySelectorAll('.yg-filter-group').forEach(remember);
    const changedGroup = input.closest('.yg-filter-group');
    if (changedGroup) {
      for (const group of form.querySelectorAll('.yg-filter-group')) {
        if (group !== changedGroup) {
          states.get(group.dataset.ygKey).scroll = 0;
          const options = group.querySelector('.yg-filter-options');
          if (options) options.scrollTop = 0;
        }
      }
    }
    if (document.activeElement === input) pendingFocus = { name: input.name, value: input.value, type: input.type };
  }, true);

  document.addEventListener('shopify:section:load', connect);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', connect, { once: true });
  else connect();
})();
